import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { spotlightApi } from '../../services/api';
import { spotlightDefaultNames, useSpotlightMarksQuery } from '../../hooks/queries';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/** Why a task is in Spotlight: put there by the person, or by one of their rules. */
export const spotlightReason = (t, reason) => (reason?.type === 'rule'
  ? t('spotlight.by_rule', { rule: reason.rule_name || t('spotlight.rules') })
  : t('spotlight.added_by_you'));

/** What went wrong, in the person's language (the backend sends a code: routes/spotlight.py). */
export const spotlightFailure = (t, err) => {
  const detail = err?.response?.data?.detail;
  const code = detail && typeof detail === 'object' ? detail.error : null;
  return code ? t(`spotlight.errors.${code}`, { ...detail, defaultValue: t('spotlight.failed') }) : t('spotlight.failed');
};

/** Puts a task in a Spotlight section, moves it, or takes it out; every view follows. */
export function useSpotlightActions() {
  const { t } = useTranslation();
  // Every Spotlight write refreshes it by itself (services/api.js).
  const fail = (err) => toast.error(spotlightFailure(t, err));
  return {
    put: (taskId, sectionId) => spotlightApi.put(taskId, sectionId).catch(fail),
    remove: (taskId) => spotlightApi.remove(taskId).catch(fail),
  };
}

/**
 * The sections a task can be in (the one it is in checked) and "Remove from Spotlight":
 * the items of a dropdown, in a task's detail and in the menu of a task row's mark.
 */
export const SpotlightMenuItems = ({ taskId, current, sections }) => {
  const { t } = useTranslation();
  const actions = useSpotlightActions();
  return (
    <>
      {sections.map((s) => (
        <DropdownMenuItem key={s.id} onSelect={() => actions.put(taskId, s.id)} data-testid="spotlight-section-option">
          <Check className={`w-4 h-4 mr-2 ${current?.section_id === s.id ? '' : 'opacity-0'}`} />{s.name}
        </DropdownMenuItem>
      ))}
      {current && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => actions.remove(taskId)} className="text-muted-foreground">
            <X className="w-4 h-4 mr-2" />{t('spotlight.remove')}
          </DropdownMenuItem>
        </>
      )}
    </>
  );
};

/** The sections and marks for menus (the default sections are made the first time). */
export function useSpotlightMarks(enabled = true, make = false) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const query = useSpotlightMarksQuery({ enabled });
  const names = useMemo(() => spotlightDefaultNames(t), [t]);
  // Someone who never opened Spotlight gets its sections (in their language) when a menu that
  // puts tasks there is shown (make: the task detail), not by merely looking at task lists.
  const made = useRef(false);
  const empty = query.isSuccess && !(query.data?.sections || []).length;
  useEffect(() => {
    if (!make || !empty || made.current) return;
    made.current = true;
    spotlightApi.open(names).then(() => qc.invalidateQueries({ queryKey: ['spotlight'] })).catch(() => {});
  }, [make, empty, names, qc]);
  const data = query.data;
  return useMemo(() => ({
    sections: data?.sections?.length ? data.sections : null,
    items: data?.items || {},
    names,
  }), [data, names]);
}

/**
 * The mark on a task row or card that is in the person's Spotlight: its section and why it is
 * there on hover, a menu to move it to another section or take it out. With `addable`, a row or
 * card that is not in Spotlight shows it faintly while the pointer is over it (its parent has the
 * `group` class), and its menu puts the task in a section: one click from any list.
 */
export const SpotlightMark = ({ taskId, mark, sections, addable = false }) => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  if (!mark && !addable) return null;
  const section = (sections || []).find((s) => s.id === mark?.section_id);
  const label = mark
    ? `${t('spotlight.title')}: ${section?.name || ''} (${spotlightReason(t, mark.reason)})`
    : t('spotlight.add_to');
  // Someone who never opened Spotlight gets its sections (in their language) on first use.
  const onOpenChange = (open) => {
    if (open && !(sections || []).length) {
      spotlightApi.open(spotlightDefaultNames(t)).then(() => qc.invalidateQueries({ queryKey: ['spotlight'] })).catch(() => {});
    }
  };
  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} title={label} aria-label={label}
          data-testid={mark ? 'spotlight-mark' : 'spotlight-add'}
          className={`flex-shrink-0 p-0.5 rounded hover:bg-amber-500/10 ${mark ? 'text-amber-500'
            : 'text-muted-foreground/60 hover:text-amber-500 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100'}`}>
          <Sparkles className="w-3 h-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {mark ? spotlightReason(t, mark.reason) : t('spotlight.add_to')}
        </DropdownMenuLabel>
        <SpotlightMenuItems taskId={taskId} current={mark} sections={sections || []} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
